import { useTranscode } from '@/hooks/useTranscodes'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { CheckCircle, Clock, Package, Film, FileText, User, Calendar } from 'lucide-react'
import type { TranscodeStatus } from '@/types/database'

interface TranscodeDetailContentProps {
  transcodeId: string
}

export function TranscodeDetailContent({ transcodeId }: TranscodeDetailContentProps) {
  const { data: transcode, isLoading, error } = useTranscode(transcodeId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading transcode details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-destructive mb-4">Failed to load transcode</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    )
  }

  if (!transcode) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Transcode not found</p>
      </div>
    )
  }

  const getStatusIcon = (status: TranscodeStatus) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusBadgeVariant = (status: TranscodeStatus) => {
    switch (status) {
      case 'complete':
        return 'default'
      case 'pending':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">{transcode.transcode_id}</h3>
          </div>
          <Badge variant={getStatusBadgeVariant(transcode.status)}>
            <span className="flex items-center gap-1">
              {getStatusIcon(transcode.status)}
              {transcode.status}
            </span>
          </Badge>
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          {transcode.vendor && (
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground">
                <User className="h-3 w-3" />
                <span>Vendor</span>
              </div>
              <p className="font-medium">{transcode.vendor}</p>
            </div>
          )}

          <div className="space-y-1">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>Created</span>
            </div>
            <p className="font-medium">
              {new Date(transcode.created_at).toLocaleDateString()}
            </p>
          </div>

          {transcode.marked_complete_date && (
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground">
                <CheckCircle className="h-3 w-3" />
                <span>Completed</span>
              </div>
              <p className="font-medium">
                {new Date(transcode.marked_complete_date).toLocaleDateString()}
              </p>
            </div>
          )}

          <div className="space-y-1">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Film className="h-3 w-3" />
              <span>Linked Shots</span>
            </div>
            <p className="font-medium">{transcode.shot_count} shot(s)</p>
          </div>
        </div>

        {/* Notes */}
        {transcode.notes && (
          <div className="space-y-2">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <FileText className="h-3 w-3" />
              <span>Notes</span>
            </div>
            <p className="text-sm bg-muted p-3 rounded-md whitespace-pre-wrap">
              {transcode.notes}
            </p>
          </div>
        )}
      </div>

      {/* Upload Result Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Upload Results</CardTitle>
          <CardDescription>Processing details from EDL upload</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">VFX Shots Linked</span>
            <span className="font-medium">{transcode.shot_count}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            If no shots were created, check that:
            <ul className="list-disc list-inside mt-1 ml-2">
              <li>Clips exist with matching tape_name and source_timecode</li>
              <li>EDL has vendor filenames in comment lines (* FROM CLIP NAME: ...)</li>
              <li>Check backend logs for detailed error messages</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Linked VFX Shots */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Linked VFX Shots</CardTitle>
          <CardDescription>
            VFX shots created or linked from this transcode request
          </CardDescription>
        </CardHeader>
        <CardContent>
          {transcode.vfx_shots && transcode.vfx_shots.length > 0 ? (
            <div className="space-y-2">
              {transcode.vfx_shots.map((shot) => (
                <div
                  key={shot.id}
                  className="flex items-center justify-between p-3 border rounded-md hover:bg-muted/50 transition-colors"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{shot.shot_name}</p>
                      <Badge variant="outline" className="text-xs">
                        {shot.status}
                      </Badge>
                    </div>
                    {shot.vendor_filename && (
                      <p className="text-xs text-muted-foreground">
                        {shot.vendor_filename}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Frames: {shot.frame_range_start}-{shot.frame_range_end} ({shot.frame_duration} frames)
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // TODO: Open VFX shot detail
                    }}
                  >
                    View
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-muted-foreground">
              No VFX shots linked to this transcode
            </div>
          )}
        </CardContent>
      </Card>

      {/* EDL Content */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">EDL Content</CardTitle>
          <CardDescription>Raw EDL file content (CMX 3600 format)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="text-xs bg-muted p-4 rounded-md overflow-x-auto max-h-[400px] overflow-y-auto font-mono">
              {transcode.edl_content}
            </pre>
            <Button
              variant="outline"
              size="sm"
              className="absolute top-2 right-2"
              onClick={() => {
                navigator.clipboard.writeText(transcode.edl_content)
              }}
            >
              Copy
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
